import { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

/**
 * Durable, per-user progress for a Moshky guided tour, backed by
 * OnboardingTourController (velaro-admin, CopilotContext).
 *
 * Server-backed rather than localStorage because the welcome modal promises "I'll keep your
 * place" — a promise localStorage breaks the moment the merchant opens the admin on a second
 * device or clears site data. Writes are fire-and-forget against a best-effort endpoint: a
 * failed save must never block the person from moving through the tour, it just means the
 * tour may greet them again next login.
 */

export const SHOPIFY_TOUR_KEY = "ShopifyWelcome";

export interface TourState {
  currentStepIndex: number;
  startedAt: string | null;
  completedAt: string | null;
  dismissedAt: string | null;
}

const EMPTY_STATE: TourState = {
  currentStepIndex: 0,
  startedAt: null,
  completedAt: null,
  dismissedAt: null
};

export default function useTourState(tourKey: string = SHOPIFY_TOUR_KEY) {
  const api = useApi();
  const [state, setState] = useState<TourState | null>(null);
  const [loading, setLoading] = useState(true);
  /** Non-null when the last progress write failed — surfaced inline in the tour, never swallowed. */
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.admin.get(`OnboardingTour/${tourKey}`);
      const data = resp.ok ? await resp.json() : null;
      setState(data && typeof data === "object" ? (data as TourState) : EMPTY_STATE);
    } catch {
      // Unreachable state store: treat as "never seen". The worst case is the tour offers
      // itself again, which is the safe direction to fail for an opt-in walkthrough.
      setState(EMPTY_STATE);
    } finally {
      setLoading(false);
    }
  }, [api, tourKey]);

  useEffect(() => {
    load();
  }, [load]);

  const post = useCallback(
    async (action: string, body?: unknown) => {
      try {
        // RestService.post(endpoint, data) sends `data` AS the JSON body — never an options bag.
        const resp = await api.admin.post(
          `OnboardingTour/${tourKey}/${action}`,
          body ?? {}
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data && typeof data === "object") setState(data as TourState);
          setSaveError(null);
          return;
        }
        setSaveError(`HTTP ${resp.status}`);
      } catch (err) {
        // Non-fatal for navigation — the person keeps moving through the tour either way — but
        // NOT silent: saveError surfaces an inline note, because "I'll keep your place" is a
        // promise the UI would otherwise break with no signal at all.
        setSaveError(err instanceof Response ? `HTTP ${err.status}` : String(err));
      }
    },
    [api, tourKey]
  );

  const recordProgress = useCallback(
    (stepIndex: number) => post("progress", { stepIndex }),
    [post]
  );
  const recordComplete = useCallback(() => post("complete"), [post]);
  const recordDismiss = useCallback(() => post("dismiss"), [post]);
  const recordReset = useCallback(() => post("reset"), [post]);

  return {
    state,
    loading,
    saveError,
    reload: load,
    recordProgress,
    recordComplete,
    recordDismiss,
    recordReset
  };
}
